/**
 * BookVehicleDialog (N2, Oct 2026) — the shared "Book Vehicle" trigger +
 * dialog used identically on all four source screens (Dispatch Draft,
 * Dispatch Lot, IBT, DC).
 *
 * Flow: open → GET /api/logistics-bookings/prefill (server derives every
 * Stage-1 field the source record can supply and lists the rest as
 * `manual_required`) → human keys the manual fields → POST
 * /api/logistics-bookings (pre-linked create — the booking lands in
 * DRAFT with this source attached; it is confirmed later from the
 * booking detail view). A 409 SOURCE_ALREADY_BOOKED surfaces verbatim,
 * naming the conflicting booking id.
 *
 * The trigger is exported alongside the dialog so all four screens render
 * a visually and behaviorally identical action.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Truck } from "lucide-react";
import { toast } from "sonner";
import PermButton from "./PermButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  vehicle_type: "",
  from_location: "",
  to_location: "",
  appointment_date: "",
  delivery_location: "",
  geo_lat: "",
  geo_long: "",
};

const FIELD_LABELS = {
  vehicle_type: "Vehicle type", from_location: "From location", to_location: "To location",
  appointment_date: "Appointment date", delivery_location: "Delivery location",
  geotag: "Geotag (latitude and longitude)",
};

export function BookVehicleButton({ onClick, disabled = false }) {
  return (
    <PermButton
      entity="LogisticsBooking"
      action="CREATE"
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      data-testid="book-vehicle-btn"
    >
      <Truck className="h-4 w-4 mr-1" /> Book Vehicle
    </PermButton>
  );
}

export default function BookVehicleDialog({ open, onClose, sourceType, sourceId, sourceLabel, onBooked }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [manualRequired, setManualRequired] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !sourceType || !sourceId) return;
    setLoading(true);
    setForm(EMPTY_FORM);
    axios
      .get(`${API}/logistics-bookings/prefill`, {
        params: { source_type: sourceType, source_id: sourceId },
      })
      .then((res) => {
        const p = res.data.prefilled || {};
        setForm({
          ...EMPTY_FORM,
          ...p,
          geo_lat: p.geotag?.lat ?? "",
          geo_long: p.geotag?.long ?? "",
        });
        setManualRequired(res.data.manual_required || []);
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Could not load booking prefill");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, sourceType, sourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vehicle type / from / to are required on every booking (server enforces).
  const ALWAYS_REQUIRED = ["vehicle_type", "from_location", "to_location"];
  const isRequired = (field) => ALWAYS_REQUIRED.includes(field) || manualRequired.includes(field);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSave = async () => {
    const missing = [...new Set([...ALWAYS_REQUIRED, ...manualRequired])].filter((field) =>
      field === "geotag"
        ? form.geo_lat === "" || form.geo_long === ""
        : !String(form[field] || "").trim());
    if (missing.length) {
      return toast.error(`Required: ${missing.map((f) => FIELD_LABELS[f] || f).join(", ")}`);
    }
    setSaving(true);
    try {
      const payload = {
        source_type: sourceType,
        source_id: sourceId,
        vehicle_type: form.vehicle_type || null,
        from_location: form.from_location || null,
        to_location: form.to_location || null,
        appointment_date: form.appointment_date || null,
        delivery_location: form.delivery_location || null,
        geotag:
          form.geo_lat !== "" && form.geo_long !== ""
            ? { lat: Number(form.geo_lat), long: Number(form.geo_long) }
            : null,
      };
      const res = await axios.post(`${API}/logistics-bookings`, payload);
      toast.success(`Draft booking ${res.data.booking_code} created — confirm it from Logistics → Bookings`);
      onBooked && onBooked(res.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // AC5 — the N1 uniqueness error (naming the conflicting booking
      // id) surfaces verbatim; no separate error path here.
      toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to create booking");
    } finally {
      setSaving(false);
    }
  };

  const textField = (key, label, type = "text") => (
    <div key={key}>
      <Label>
        {label} {isRequired(key) && <span className="text-rose-600">*</span>}
      </Label>
      <Input
        type={type}
        value={form[key]}
        onChange={set(key)}
        data-testid={`bv-field-${key}`}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving && !o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="book-vehicle-dialog">
        <DialogHeader>
          <DialogTitle>Book Vehicle</DialogTitle>
          <DialogDescription>
            {sourceLabel || `${sourceType} ${sourceId}`} — a DRAFT logistics
            booking will be created, linked to this record. Confirm it later
            from the booking detail.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-sm text-zinc-500" data-testid="book-vehicle-loading">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <Label>
                Vehicle type (ft) <span className="text-rose-600">*</span>
              </Label>
              <Select value={form.vehicle_type} onValueChange={set("vehicle_type")}>
                <SelectTrigger data-testid="bv-field-vehicle_type">
                  <SelectValue placeholder="32 / 20 / 14" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="32">32 ft</SelectItem>
                  <SelectItem value="20">20 ft</SelectItem>
                  <SelectItem value="14">14 ft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {textField("from_location", "From location")}
            {textField("to_location", "To location")}
            {textField("delivery_location", "Delivery location")}
            {textField("appointment_date", "Appointment date", "date")}
            <div className="col-span-2">
              <Label>
                Geotag (lat, long) {isRequired("geotag") && <span className="text-rose-600">*</span>}
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="any" placeholder="Latitude"
                  value={form.geo_lat} onChange={set("geo_lat")} data-testid="bv-field-geo_lat" />
                <Input type="number" step="any" placeholder="Longitude"
                  value={form.geo_long} onChange={set("geo_long")} data-testid="bv-field-geo_long" />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <PermButton entity="LogisticsBooking" action="CREATE" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </PermButton>
          <PermButton
            entity="LogisticsBooking"
            action="CREATE"
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="book-vehicle-save-btn"
          >
            {saving ? "Booking…" : "Create booking"}
          </PermButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
